<script setup lang="ts">
import { ref } from "vue"
import { useHttpClient } from "@/vf"

const form = ref<{
    name: string
    email: string
    platform: string
    os: string
    browser: string
    device: string
    message: string
}>({
    name: "",
    email: "",
    platform: "",
    os: "",
    browser: "",
    device: "",
    message: "",
})

const http = useHttpClient()

async function send() {
    const result = await http.post("/accessibility/feedback", form.value)
    if (result.status === 204) {
        alert("Vielen Dank für deine Rückmeldung!")
        form.value = {
            name: "",
            email: "",
            platform: "",
            os: "",
            browser: "",
            device: "",
            message: "",
        }
    } else {
        alert("Es gab ein Problem beim Senden deiner Nachricht. Bitte versuche es später erneut.")
    }
}
</script>

<template>
    <main aria-labelledby="main-heading">
        <section class="container">
            <div class="breadcrumb">
                <RouterLink class="breadcrumb-item" :to="{ name: 'home' }">Home</RouterLink>
                <div class="breadcrumb-item">Barrierefreiheit</div>
            </div>
        </section>

        <section class="container mt-5 text-justify">
            <h1 id="main-heading">Informationen zur Barrierefreiheit</h1>
            <p dir="ltr">gemäß Barrierefreiheitsstärkungsgesetz (BFSG)</p>
            <h2>1. Beschreibung der Dienstleistung</h2>
            <p dir="ltr">
                CANN24 (apotheke am inselpark e.K., Neuenfelder Straße 31, 21109 Hamburg) ist eine Online-Apotheke für
                verschreibungspflichtiges medizinisches Cannabis. Über unsere Webseite können unsere Kunden
                Cannabisprodukte und Zubehörartikel bestellen. Wir beliefern unsere Kunden deutschlandweit per Versand.
                Alternativ ist auch eine persönliche Abholung der Produkte in unserer Filiale in Hamburg möglich.
            </p>
            <p dir="ltr">
                Wir bemühen uns um eine barrierefreie Nutzung unserer Webseite nach dem Barrierefreiheitsstärkungsgesetz
                (BFSG). Die hier aufgeführten Informationen zur Barrierefreiheit beziehen sich auf unsere Webseite
                CANN24.de (digitale Dienstleistung).
            </p>
            <h2>2. Nutzungserläuterungen</h2>
            <h3>2.1 Vor der Bestellung</h3>
            <p dir="ltr">
                Um unser Angebot nutzen zu können, müssen Sie sich zunächst registrieren. Wenn Ihr Profil von uns
                freigeschaltet wurde und Sie sich eingeloggt haben, haben Sie Zugriff auf unser Sortiment. Unser
                Sortiment ist in verschiedene Produktkategorien unterteilt. Verschiedene Filter sowie eine Suchfunktion
                erleichtern das Finden von Produkten. Die Suchfunktion finden Sie rechts oben auf dem Bildschirm.
            </p>
            <h3>2.2 Bestellung</h3>
            <p dir="ltr">
                Produkte können mittels eines Buttons auf der Produktseite dem Warenkorb hinzugefügt werden. Im
                Warenkorb sind nacheinander Angaben zu Bezahlung, Versand und Lieferung erforderlich. Ebenfalls muss
                hier angegeben werden, über welche Art von Rezept Sie verfügen und auf welchem Wege es zu uns kommt. Sie
                können das Rezept hier auch hochladen. Auf der letzten Seite im Warenkorb sehen Sie eine Zusammenfassung
                der Informationen zu Ihrer Bestellung. Hier werden Sie gebeten, einige Dinge zu bestätigen, wie etwa
                unsere Allgemeinen Geschäftsbedingungen. Sollten Sie an dieser Stelle noch eine Änderung an Ihrer
                Bestellung vornehmen wollen, können Sie einfach zu jedem vorherigen Schritt der Bestellung zurückgehen.
                Andernfalls können Sie Ihre Bestellung hier abschließen.
            </p>
            <h3>2.3 Nach der Bestellung</h3>
            <p dir="ltr">
                Wir können Ihre Bestellung nur bearbeiten, wenn die bestellte Sorte und Menge exakt mit Ihrem Rezept
                übereinstimmen. Zur Bearbeitung muss uns außerdem das Originalrezept (oder das digitale Rezept)
                vorliegen. Papierrezepte können uns per Post zugesandt oder persönlich vorbeigebracht werden. Wenn das
                Rezept erfolgreich von uns geprüft wurde und auch die Bezahlung eingegangen ist, bereiten wir Ihre
                Bestellung für den Versand oder die Abholung vor.
            </p>
            <h3>2.4 Empfohlener Browser</h3>
            <p dir="ltr">
                Für die Nutzung unserer Webseite empfehlen wir einen der folgenden Browser in der jeweils aktuellen
                Version: Firefox, Google Chrome, Safari.
            </p>
            <h2>3. Erfüllungsgrad</h2>
            <p dir="ltr">
                Unsere Webseite wurde intern mittels manueller und automatisierter Prüfverfahren auf Barrierefreiheit
                überprüft. Unsere Webseite entspricht teilweise den Anforderungen an die Barrierefreiheit (siehe
                Abschnitt 3.1 und 3.2). <br />
                <br />
                Die Umsetzung der Anforderungen basiert auf den geltenden technischen Spezifikationen und Normen,
                insbesondere der Norm EN 301 549 und der Richtlinien der WCAG 2.1 - Konformitätsstufe AA. <br />
                <br />
                Sollten bislang unerkannte Barrieren bestehen, werden wir diese zeitnah und im Rahmen unserer
                technischen und wirtschaftlichen Möglichkeiten beseitigen.
            </p>
            <p dir="ltr">
                Wir streben an, neue Funktionen, die wir in Zukunft in unsere Webseite einbinden, barrierefrei zu
                gestalten. Wir werden unsere Webseite auch im Gesamten regelmäßig auf Barrierefreiheit überprüfen.
            </p>
            <h3>3.1 Umgesetzte Aspekte</h3>
            <p dir="ltr">Umgesetzt wurden unter anderem folgende Aspekte:</p>
            <ul>
                <li>Tastaturbedienbarkeit aller Funktionen</li>
                <li>Lesbarkeit mit einem Screenreader</li>
                <li>Lesbarkeit der Seite bei Farbenblindheit</li>
                <li>Alternativtexte für Bilder</li>
                <li>Verständliche Sprache</li>
                <li>Skalierbarkeit</li>
                <li>
                    Verständliche Bezeichnungen von Formularfeldern und Buttons, einschließlich verständlicher
                    Fehlermeldungen
                </li>
                <li>Strukturierte Überschriften <br /></li>
            </ul>
            <h3>3.2 Noch umzusetzende Aspekte</h3>
            <p dir="ltr">
                Nachfolgende Aspekte sind aus den folgenden Gründen noch nicht barrierefrei. Wir arbeiten daran, diese
                so zeitnah wie möglich anzupassen.
            </p>
            <ul>
                <li>
                    Ausreichende Kontraste: Die Kontraste auf unserer Webseite sind teilweise noch nicht ausreichend, da
                    eine einfache Erhöhung der Kontraste die allgemeine Nutzbarkeit unserer Webseite eingeschränkt
                    hätte.
                </li>
            </ul>
            <h2>4. Marktüberwachungsbehörde</h2>
            <p dir="ltr">
                Zuständig für die Überwachung der Barrierefreiheit ist die Marktüberwachungsstelle der Länder für die
                Barrierefreiheit von Produkten und Dienstleistungen (MLBF) mit Sitz in Magdeburg, Sachsen-Anhalt. <br />
            </p>
            <p dir="ltr">Datum der Erstellung dieser Information: 25.06.2025</p>
            <h2>Kontakt zur Barrierefreiheit</h2>
            <p dir="ltr">
                Falls Ihnen auf unserer Webseite Barrieren auffallen oder Sie Fragen zur Barrierefreiheit haben, füllen
                Sie bitte folgendes Formular aus. Wir beantworten Ihr Anliegen in der Regel spätestens innerhalb von
                vier Wochen.
            </p>
        </section>

        <form class="container mt-3" @submit.prevent="send()">
            <div class="mt-3">
                <label for="name">Name</label>
                <input id="name" v-model="form.name" type="text" class="form-control" />
            </div>
            <div class="mt-3">
                <label for="email">E-Mail (für Rückfragen oder Rückmeldung)</label>
                <input id="email" v-model="form.email" type="email" class="form-control" />
            </div>
            <div class="mt-3">
                <label for="platform">Verwendetes Gerät</label>
                <select id="platform" v-model="form.platform" class="form-control custom-select">
                    <option value="" disabled selected>Bitte wählen</option>
                    <option value="desktop">Desktop</option>
                    <option value="mobile">Mobil</option>
                    <option value="other">Anderes</option>
                </select>
            </div>
            <div class="mt-3">
                <label for="os">Betriebssystem</label>
                <select id="os" v-model="form.os" class="form-control custom-select">
                    <option value="" disabled selected>Bitte wählen</option>
                    <option value="windows">Windows</option>
                    <option value="macos">macOS</option>
                    <option value="linux">Linux</option>
                    <option value="android">Android</option>
                    <option value="ios">iOS</option>
                    <option value="other">Anderes</option>
                </select>
            </div>
            <div class="mt-3">
                <label for="browser">Browser</label>
                <select id="browser" v-model="form.browser" class="form-control custom-select">
                    <option value="" disabled selected>Bitte wählen</option>
                    <option value="chrome">Chrome</option>
                    <option value="firefox">Firefox</option>
                    <option value="safari">Safari</option>
                    <option value="edge">Edge</option>
                    <option value="opera">Opera</option>
                    <option value="other">Anderes</option>
                </select>
            </div>
            <div class="mt-3">
                <label for="device">Verwendetes Hilfsmittel / Reader / Software</label>
                <input id="device" v-model="form.device" type="text" class="form-control" />
            </div>
            <div class="mt-3">
                <label for="issue">Mitteilung</label><span class="small text-muted ml-1">(Pflichtfeld)</span>
                <textarea
                    id="issue"
                    v-model="form.message"
                    aria-required="true"
                    class="form-control"
                    rows="8"
                    required
                ></textarea>
            </div>
            <div class="mt-3">
                <button class="btn btn-primary">Absenden</button>
            </div>
        </form>
    </main>
</template>

<style scoped>
h2 {
    margin-top: 3rem;
    color: black;
}

h3 {
    margin-top: 2.5rem;
}
</style>
